// @ts-strict-ignore
import {
    AppPlatform,
    AppStoreType,
    ChecklistItem,
    ChecklistItemPlacement,
    ChecklistItemStatusType,
    ReleaseStepType,
    SubscriptionFeatureType,
} from '../types';
import * as React from 'react';
import { CheckSquareOutlined, MenuOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../reducers/reducers';
import {
    getError,
    getIsLoading,
    getUpdated,
    isCompleteChecklistItemStatus,
    isStepChecklistPlacement,
} from '../helpers';
import {
    Nullable,
    NumberKeyedDictionary,
    ReleaseStepComponentProps,
    StepRunComponentProps,
} from '../typescriptHelpers';
import { UpdateChecklistItemStatusRequest } from '../actions/release';
import { AppSettingsV2Updatable, ChecklistItemUpdatable } from '../actions/updatable';
import { forEach } from 'lodash';
import { antGrey } from './styles/globalStyles';
import { ReleaseStepDeleteChecklistItemRequest, tryUpdateAppSettingsV2 } from '../actions/appSettings';
import Table, { ColumnType } from 'antd/lib/table';
import ChecklistItemRowComponent from './ChecklistItemRowComponent';
import ChecklistItemAdditionalInfoModal, { ChecklistItemInfo } from './ChecklistItemAdditionalInfoModal';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import { displayTextForReleaseStepType } from '../helpers/displayHelpers';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import {
    selectAppPlatform,
    selectAppStoreType,
    selectOrgGroups,
    selectReleaseVersion,
    selectUser,
    selectIsWorkflowsCIStepEnabled,
} from '../selectors/selectors';
import ExpandedChecklistItemRowComponent from './checklistItems/ExpandedChecklistItemRowComponent';
import { arrayMoveImmutable } from 'array-move';
import CLIEditActions from './checklistItems/CLIEditActionsComponent';
import { globalMessage } from '../utils/globalMessage';
import { PlanRestrictedFeatureWrapper } from './planRestrictions/PlanRestrictedFeatureWrapper';
import { tryUpdateChecklistItem } from '@/actions/checklistItems';
import { clearFailure } from '@/actions/failed';
import { useCallUpdatableForCollections } from '@/utils/hooks/helpers';
import { StepRunUpdatable } from '@/actions/stepRun';
import { StepRunChecklistItemEntityRequest } from '@/actions/entities';

const DragHandle = SortableHandle((props) => <MenuOutlined style={{ cursor: 'pointer', ...props.style }} size={21} />);
const SortableTableItem = SortableElement((props) => <tr {...props} />);
const SortableTableContainer = SortableContainer((props) => <tbody {...props} />);

interface StepChecklistItemProps extends ReleaseStepComponentProps {
    isEditing: boolean;
    items: ChecklistItem[];
    onEditChecklistItem: (id: string) => void;
    onAddComment: (id: string) => void;
    placement: ChecklistItemPlacement;
    onUpdateOrder: (items: ChecklistItem[]) => void;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    shouldDisplayInReadOnlyMode?: boolean;
}

export const StepChecklistItems: React.FC<StepChecklistItemProps> = (props) => {
    const dispatch = useDispatch();

    const keyedItems = props.items.map((item) => {
        return {
            ...item,
            key: item.id,
        };
    });

    const userId = useSelector((state: RootState) => selectUser(state).id);

    const releaseVersion = useSelector((state: RootState) => selectReleaseVersion(state, props.releaseId));

    const [
        deleteChecklistItemCollection,
        deleteChecklistItemIsLoadingCollection,
        ,
        deleteChecklistItemErrorCollection,
    ] = useCallUpdatableForCollections(
        StepRunUpdatable.DeleteStepRunChecklistItem,
        props.items.reduce<Record<string, StepRunChecklistItemEntityRequest>>((acc, item) => {
            acc[item.id] = {
                ...props.stepRunComponentProps,
                checklistItemId: item.id,
            };
            return acc;
        }, {}),
        props.items.map((item) => item.id),
    );

    const [
        updateChecklistItemStatusCollection,
        updateChecklistItemIsLoadingCollection,
        updateChecklistItemResponseTypeCollection,
        updateChecklistItemStatusErrorCollection,
    ] = useCallUpdatableForCollections(
        StepRunUpdatable.UpdateChecklistItemStatus,
        props.items.reduce<Record<string, StepRunChecklistItemEntityRequest>>((acc, item) => {
            acc[item.id] = {
                ...props.stepRunComponentProps,
                checklistItemId: item.id,
            };
            return acc;
        }, {}),
        props.items.map((item) => item.id),
    );

    const {
        releaseStepItemUpdateError,
        releaseStepItemDeleteIsLoading,
        releaseStepItemDeleteError,
        releaseStepItemUpdateSuccess,
        releaseStepItemUpdateIsLoading,
    } = useSelector((state: RootState) => {
        const releaseStepItemUpdateError: NumberKeyedDictionary<string> = {};
        const releaseStepItemDeleteIsLoading: NumberKeyedDictionary<boolean> = {};
        const releaseStepItemDeleteError: NumberKeyedDictionary<string> = {};
        const releaseStepItemUpdateSuccess: NumberKeyedDictionary<boolean> = {};
        const releaseStepItemUpdateIsLoading: NumberKeyedDictionary<boolean> = {};
        forEach(props.items, (item: ChecklistItem) => {
            const updateSuccess = getUpdated(ChecklistItemUpdatable.ChecklistItemStatusChanged, state, item.id);
            const updateLoading = getIsLoading(ChecklistItemUpdatable.ChecklistItemStatusChanged, state, item.id);
            const updateFailed = getError(ChecklistItemUpdatable.ChecklistItemStatusChanged, state, item.id);

            const deleteIsLoading = getIsLoading(AppSettingsV2Updatable.DeleteReleaseStepChecklistItem, state, item.id);
            const deleteError = getError(AppSettingsV2Updatable.DeleteReleaseStepChecklistItem, state, item.id);

            releaseStepItemDeleteIsLoading[item.id] = deleteIsLoading;
            releaseStepItemDeleteError[item.id] = deleteError;

            releaseStepItemUpdateError[item.id] = updateFailed;
            releaseStepItemUpdateIsLoading[item.id] = updateLoading;
            releaseStepItemUpdateSuccess[item.id] = updateSuccess;
        });

        return {
            releaseStepItemUpdateError,
            releaseStepItemDeleteIsLoading,
            releaseStepItemUpdateSuccess,
            releaseStepItemUpdateIsLoading,
            releaseStepItemDeleteError,
        };
    });

    const itemUpdateError =
        props.stepRunComponentProps ? updateChecklistItemStatusErrorCollection : releaseStepItemUpdateError;

    const itemDeleteIsLoading =
        props.stepRunComponentProps ? deleteChecklistItemIsLoadingCollection : releaseStepItemDeleteIsLoading;
    const itemDeleteError =
        props.stepRunComponentProps ? deleteChecklistItemErrorCollection : releaseStepItemDeleteError;
    const itemUpdateSuccess =
        props.stepRunComponentProps ? updateChecklistItemResponseTypeCollection : releaseStepItemUpdateSuccess;
    const itemUpdateIsLoading =
        props.stepRunComponentProps ? updateChecklistItemIsLoadingCollection : releaseStepItemUpdateIsLoading;

    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, props.appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));
    const isWorkflowsCIStepEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsCIStepEnabled(state, props.appId),
    );
    // do not change the order of the original items array
    const copyOfItems = [...props.items];
    copyOfItems.sort((item1, item2) => (item1.createdAt < item2.createdAt ? -1 : 1));
    let disabledItemReferenceDate = '';
    if (copyOfItems.length > 5) {
        disabledItemReferenceDate = copyOfItems[4].createdAt;
    }

    const emptyStateText = emptyTextForChecklistOnReleaseStep(
        props.releaseStepType,
        props.placement,
        appPlatform,
        appStoreType,
        isWorkflowsCIStepEnabled,
    );

    const orgGroupsById = useSelector((state: RootState) => selectOrgGroups(state, props.orgId));

    const tableContainer = React.useRef<HTMLDivElement>(null);
    const [itemStatus, setItemStatus] = React.useState<NumberKeyedDictionary<ChecklistItemStatusType>>({});
    const [showItemDescription, setShowItemDescription] = React.useState(false);
    const [itemInfo, setItemInfo] = React.useState<ChecklistItemInfo | null>(null);
    const [itemsIdOrder, setItemsIdOrder] = React.useState<String[]>(keyedItems.map((item) => item.id));
    const [shownUpdateErrors, setShownUpdateErrors] = React.useState<Set<string>>(new Set());
    const [shownDeleteErrors, setShownDeleteErrors] = React.useState<Set<string>>(new Set());

    const updateChecklistItemStatus = (requestData: UpdateChecklistItemStatusRequest, id: string) => {
        if (props.stepRunComponentProps) {
            updateChecklistItemStatusCollection[id](requestData);
        } else {
            dispatch(tryUpdateChecklistItem(ChecklistItemUpdatable.ChecklistItemStatusChanged, requestData, id));
        }
    };

    const deleteChecklistItem = (requestData: ReleaseStepDeleteChecklistItemRequest) => {
        if (props.stepRunComponentProps) {
            deleteChecklistItemCollection[requestData.checklistItemId]();
        } else {
            dispatch(
                tryUpdateAppSettingsV2(
                    AppSettingsV2Updatable.DeleteReleaseStepChecklistItem,
                    requestData,
                    requestData.checklistItemId,
                ),
            );
        }
    };

    const clearItemFailure = (id: string) => {
        dispatch(clearFailure(ChecklistItemUpdatable.ChecklistItemStatusChanged, id));
    };

    React.useEffect(() => {
        forEach(itemUpdateError, (error, id) => {
            if (error && !shownUpdateErrors.has(id)) {
                globalMessage.error(error, () => {
                    clearItemFailure(id);
                    setShownUpdateErrors((prev) => {
                        const next = new Set(prev);
                        next.delete(id);
                        return next;
                    });
                });
                setShownUpdateErrors((prev) => new Set(prev).add(id));
            }
        });
    }, [itemUpdateError]);

    React.useEffect(() => {
        const updatedItemStatus = { ...itemStatus };
        forEach(itemUpdateError, (error, key) => {
            if (error) {
                updatedItemStatus[key] = undefined;
            }
        });
        setItemStatus(updatedItemStatus);
    }, [JSON.stringify(Object.values(itemUpdateError))]);

    React.useEffect(() => {
        forEach(itemUpdateSuccess, (successOrResponseType) => {
            if (successOrResponseType) {
                if (props.placement !== ChecklistItemPlacement.StepChecklist) {
                    globalMessage.success('Status updated successfully!');
                }
            }
        });
    }, [JSON.stringify(Object.values(itemUpdateSuccess))]);

    React.useEffect(() => {
        forEach(itemDeleteError, (error, id) => {
            if (error && !shownDeleteErrors.has(id)) {
                globalMessage.error(error, () => {
                    clearItemFailure(id);
                    setShownDeleteErrors((prev) => {
                        const next = new Set(prev);
                        next.delete(id);
                        return next;
                    });
                });
                setShownDeleteErrors((prev) => new Set(prev).add(id));
            }
        });
    }, [JSON.stringify(Object.values(itemDeleteError))]);

    const itemStatusChanged = (id: string, status?: ChecklistItemStatusType) => {
        setItemStatus((prevState) => {
            const updatedItemStatus = { ...prevState, [id]: status };
            return updatedItemStatus;
        });

        clearItemFailure(id);
        setShownUpdateErrors((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });

        const requestData: UpdateChecklistItemStatusRequest = {
            orgId: props.orgId,
            userId,
            checklistSettingId: id,
            releaseId: props.releaseId,
            appId: props.appId,
            status,
        };

        updateChecklistItemStatus(requestData, id);
    };

    const deleteItem = (id: string) => {
        const requestData: ReleaseStepDeleteChecklistItemRequest = {
            appId: props.appId,
            orgId: props.orgId,
            checklistItemId: id,
        };

        deleteChecklistItem(requestData);
    };

    const onSortEnd = ({ oldIndex, newIndex }) => {
        if (oldIndex !== newIndex) {
            const rearrangedItems = arrayMoveImmutable(keyedItems, oldIndex, newIndex);
            const itemsIdOrder = rearrangedItems.map((item) => item.id);
            const sortedItems = keyedItems.slice().sort((a, b) => {
                return itemsIdOrder.indexOf(a.id) - itemsIdOrder.indexOf(b.id);
            });
            setItemsIdOrder(itemsIdOrder);
            props.onUpdateOrder(sortedItems);
        }
    };

    const DraggableContainer = (props) => (
        <SortableTableContainer
            useDragHandle
            helperClass="row-dragging"
            lockAxis="y"
            helperContainer={tableContainer.current}
            onSortEnd={onSortEnd}
            {...props}
        />
    );

    const DraggableBodyRow = (props) => {
        const id = props['data-row-key'];
        const index = keyedItems.findIndex((item) => item.id === id);
        return <SortableTableItem index={index} {...props} />;
    };

    const shouldDisable = (item: ChecklistItem, showRestrictedUI: boolean) => {
        if (keyedItems.length < 6) {
            return false;
        }
        if (showRestrictedUI) {
            return item.createdAt > disabledItemReferenceDate;
        }
        return false;
    };

    const renderExpandedChecklistItem = (item: ChecklistItem) => {
        const localItemStatus = itemStatus[item.id];
        const status =
            localItemStatus !== undefined ? localItemStatus
            : item.status?.status ? item.status.status
            : null;
        const isEditing = props.isEditing;
        const showAsComplete = status ? isCompleteChecklistItemStatus(status) : false;
        const isLoading = itemUpdateIsLoading[item.id];

        return (
            <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI}>
                {(isDisabled) => (
                    <ExpandedChecklistItemRowComponent
                        {...item}
                        orgId={props.orgId}
                        appId={props.appId}
                        releaseId={props.releaseId}
                        isEditing={isEditing}
                        showAsComplete={showAsComplete}
                        isLoading={isLoading}
                        showStatus={status}
                        onClickInfo={(id, title, description) => {
                            setItemInfo({ title, description });
                            setShowItemDescription(true);
                        }}
                        stepRunComponentProps={props.stepRunComponentProps}
                        shouldDisplayInReadOnlyMode={props.shouldDisplayInReadOnlyMode}
                        onChangeStatus={(status, id) => itemStatusChanged(id, status)}
                        onAddComment={props.onAddComment}
                        isDisabled={shouldDisable(item, isDisabled)}
                        userGroups={orgGroupsById}
                        dueDate={item.resolvedTargetCompletionDate}
                        targetCompletion={item.targetCompletion}
                    />
                )}
            </PlanRestrictedFeatureWrapper>
        );
    };

    const renderChecklistItem = (item: ChecklistItem) => {
        const localItemStatus = itemStatus[item.id];
        const status =
            localItemStatus !== undefined ? localItemStatus
            : item.status?.status ? item.status.status
            : null;
        const isChecked = status ? isCompleteChecklistItemStatus(status) : false;
        const isEditing = props.isEditing;

        const isOneOff =
            !!props.stepRunComponentProps && (item as ChecklistItem)?.stepRunId && !(item as ChecklistItem).templateId;
        return (
            <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI}>
                {(isDisabled) => (
                    <ChecklistItemRowComponent
                        orgId={props.orgId}
                        appId={props.appId}
                        releaseId={props.releaseId}
                        isEditing={isEditing}
                        {...item}
                        oneOffForVersion={isOneOff ? releaseVersion : item.oneOffForVersion}
                        showAsComplete={isChecked}
                        onClickInfo={(id, title, description) => {
                            setItemInfo({ title, description });
                            setShowItemDescription(true);
                        }}
                        itemStatusChanged={itemStatusChanged}
                        stepRunComponentProps={props.stepRunComponentProps}
                        shouldDisplayInReadOnlyMode={props.shouldDisplayInReadOnlyMode}
                        isDisabled={shouldDisable(item, isDisabled)}
                        orgGroups={orgGroupsById}
                        dueDate={item?.resolvedTargetCompletionDate}
                        targetCompletion={item?.targetCompletion}
                    />
                )}
            </PlanRestrictedFeatureWrapper>
        );
    };

    const renderEditButtons = (item: ChecklistItem) => {
        const singleItemDeleteIsLoading = itemDeleteIsLoading[item.id];

        return (
            <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI}>
                {(isDisabled) => (
                    <CLIEditActions
                        orgId={props.orgId}
                        itemId={item.id}
                        deleteIsLoading={singleItemDeleteIsLoading}
                        onConfirmDelete={deleteItem}
                        onEditChecklistItem={props.onEditChecklistItem}
                        disableEditButton={shouldDisable(item, isDisabled)}
                        shouldDisableOrgLevelCliActions={!!item.orgTemplateId}
                    />
                )}
            </PlanRestrictedFeatureWrapper>
        );
    };

    const renderDrag = (item: ChecklistItem) => {
        const isOneOff = !!props.stepRunComponentProps && item.stepRunId && !item.templateId;

        if (isOneOff) {
            return null;
        }

        const baseStyle: React.CSSProperties = { paddingLeft: 12 };
        return <DragHandle style={{ ...baseStyle, color: antGrey.grey7 }} />;
    };

    const columns: ColumnType<ChecklistItem>[] = [];

    if (props.isEditing) {
        columns.push({
            colSpan: 0,
            width: 50,
            className: 'drag-visible',
            render: renderDrag,
        });
    }

    if (isStepChecklistPlacement(props.placement)) {
        columns.push({
            colSpan: 0,
            className: 'drag-visible',
            render: renderChecklistItem,
        });
    } else {
        columns.push({
            colSpan: 0,
            className: 'drag-visible',
            render: renderExpandedChecklistItem,
        });
    }

    if (props.isEditing) {
        columns.push({
            colSpan: 0,
            width: 100,
            align: 'center',
            render: renderEditButtons,
        });
    }

    const hasChecklistItems = keyedItems.length > 0;

    let oneOffItems = [];
    let normalItems = [];

    const sortedItems = keyedItems.slice();

    if (props.isEditing) {
        sortedItems.sort((a, b) => {
            return itemsIdOrder.indexOf(a.id) - itemsIdOrder.indexOf(b.id);
        });
    }

    sortedItems.forEach((item) => {
        if (props.stepRunComponentProps && item.stepRunId && !item.templateId) {
            oneOffItems.push(item);
        } else {
            normalItems.push(item);
        }
    });

    const tableSize = props.shouldDisplayInReadOnlyMode ? 'small' : 'large';

    return (
        <div ref={tableContainer} style={{ display: 'flex', justifyContent: 'stretch', flexDirection: 'column' }}>
            {hasChecklistItems && (
                <>
                    {(normalItems.length > 0 || oneOffItems.length === 0) && (
                        <Table<ChecklistItem>
                            className="step-checklist-items-table"
                            size={tableSize}
                            locale={{ emptyText: 'No items' }}
                            pagination={false}
                            dataSource={normalItems}
                            columns={columns}
                            components={{
                                body: {
                                    wrapper: DraggableContainer,
                                    row: DraggableBodyRow,
                                },
                            }}
                            style={{ flexGrow: 1, width: '100%' }}
                            onRow={(item) => {
                                return {
                                    style:
                                        !!item.orgTemplateId && props.isEditing ?
                                            { backgroundColor: antGrey.grey2, opacity: 0.8 }
                                        :   {},
                                };
                            }}
                        />
                    )}
                    {oneOffItems.length > 0 && (
                        <Table
                            className="step-checklist-items-table"
                            size={tableSize}
                            locale={{ emptyText: null }}
                            pagination={false}
                            dataSource={oneOffItems}
                            columns={columns}
                            style={{ flexGrow: 1, width: '100%' }}
                        />
                    )}
                </>
            )}
            {!hasChecklistItems && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexGrow: 1,
                        height: 200,
                    }}
                >
                    <Space>
                        <CheckSquareOutlined style={{ color: antGrey.grey7 }} />
                        <Text style={{ fontSize: 16 }} type="secondary">
                            {emptyStateText}
                        </Text>
                    </Space>
                </div>
            )}
            <ChecklistItemAdditionalInfoModal
                title={itemInfo?.title ?? ''}
                description={itemInfo?.description ?? ''}
                isVisible={!!showItemDescription}
                onCancel={() => setShowItemDescription(false)}
                afterClose={() => setItemInfo(null)}
            />
        </div>
    );
};

const emptyTextForChecklistOnReleaseStep: (
    releaseStep: ReleaseStepType,
    placement: ChecklistItemPlacement,
    platform: AppPlatform,
    appStoreType: AppStoreType,
    isWorkflowsCIStepEnabled: boolean,
) => string = (releaseStep, placement, platform, appStoreType, isWorkflowsCIStepEnabled) => {
    if (isStepChecklistPlacement(placement)) {
        return `Add checklist items to capture ${displayTextForReleaseStepType(
            releaseStep,
            platform,
            appStoreType,
            isWorkflowsCIStepEnabled,
        )} tasks for your unique release process.`;
    } else {
        return `Add ${displayTextForReleaseStepType(releaseStep, platform, appStoreType, isWorkflowsCIStepEnabled)} items.`;
    }
};
